import React, { useEffect, useState, useCallback } from 'react';

import { UseOutletContext } from '../Main';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { useCatalog } from '../../../contexts/CatalogContext';
import { ApiDigitalAuthSubscription } from '../../../services/auth/subscription.service';
import { authApiKey } from '../../../scripts/authentication.script';
import { dateFormat } from '../../../scripts/date.time.format.script';
import { featureLabel } from '../../../scripts/feature.labels';
import { GlobalSwal } from '../../../styles/swal.style';
import { CatalogAddOn } from '../../../models/catalog.addon.model';

interface ActiveAddonRecord {
  _id: string;
  addon_id: CatalogAddOn | string;
  status: string;
  activated_at: string;
  cancelled_at: string | null;
  cancel_at_period_end: boolean;
  current_period_start: string;
  current_period_end: string;
}

const SubscriptionAddonsPage: React.FunctionComponent = () => {
  const {setRoute} = UseOutletContext();
  const {lang} = StateLanguage();
  const { snapshot, refreshSubscription, can: canSub } = useSubscription();
  const { addons, loading: catalogLoading } = useCatalog();
  const [mounted, setMounted] = useState(false);
  const [activeAddons, setActiveAddons] = useState<ActiveAddonRecord[] | null>(null);
  const [loadingActive, setLoadingActive] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const canManage = canSub('manage', 'digital:auth:entity:subscription:*').allowed;
  const subId = snapshot?.subscription?.id;
  const currentPlanId = snapshot?.subscription?.plan?.id;
  const isTrial = snapshot?.subscription?.status === 'trial';
  const trialEnd = snapshot?.subscription?.trial_end;

  useEffect(() => {
    setMounted(true);
    setRoute({path: {root: lang.labels.administration, branch: lang.labels.addonSectionTitle}, company: true});
    return () => setMounted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const interpolate = (template: string, vars: Record<string, string | number>): string => {
    let result = template;
    for (const [key, value] of Object.entries(vars)) {
      result = result.replace(`{${key}}`, String(value));
    }
    return result;
  };

  const fmtDate = useCallback((iso?: string): string => {
    if (!iso) return '';
    return dateFormat(lang.code, iso);
  }, [lang.code]);

  // Load active add-ons for this subscription
  const loadActiveAddons = useCallback(async () => {
    const apiKey = authApiKey();
    if (!apiKey || !subId) return;

    setLoadingActive(true);
    try {
      const response = await ApiDigitalAuthSubscription.getSubscriptionAddons(apiKey, subId);
      if (response.status === 200 && response.data) {
        const data = response.data;
        const list: ActiveAddonRecord[] = Array.isArray(data) ? data : (data?.addons ?? []);
        setActiveAddons(list);
      } else {
        setActiveAddons([]);
      }
    } catch {
      setActiveAddons([]);
    } finally {
      setLoadingActive(false);
    }
  }, [subId]);

  useEffect(() => {
    if (subId) {
      loadActiveAddons();
    }
  }, [subId, loadActiveAddons]);

  // Find active record for an addon
  const findActiveRecord = (addonId: string): ActiveAddonRecord | undefined => {
    if (!activeAddons) return undefined;
    return activeAddons.find(a => {
      const aId = typeof a.addon_id === 'object' ? (a.addon_id as CatalogAddOn)?.id ?? (a.addon_id as any)?._id : a.addon_id;
      return aId === addonId && a.status === 'active';
    });
  };

  // Is addon compatible with current plan?
  const isCompatible = (addon: CatalogAddOn): boolean => {
    const compat = addon.compatible_plans;
    if (!compat || compat.length === 0) return true;
    return !!currentPlanId && compat.includes(currentPlanId);
  };

  // Format addon price with currency and cycle
  const formatPrice = (addon: CatalogAddOn): string => {
    const price = addon.price;
    const code = addon.currency?.code ?? 'CHF';
    if (price === undefined || price === null) return '';
    const cycle = addon.billing_cycle;
    const suffix = cycle === 'yearly' ? ` ${lang.labels.addonBillingCycleYearly}`
                 : cycle === 'monthly' ? ` ${lang.labels.addonBillingCycleMonthly}`
                 : cycle === 'one_time' ? ` (${lang.labels.addonBillingCycleOneTime})`
                 : '';
    return `${code} ${price.toFixed(2)}${suffix}`;
  };

  // Feature names for an addon
  const addonFeatureNames = (addon: CatalogAddOn): string => {
    if (!addon.features) return '';
    const keys = Object.entries(addon.features)
      .filter(([, v]) => v === true)
      .map(([k]) => featureLabel(lang, k));
    return keys.join(', ');
  };

  // --- Actions ---

  const handleActivate = async (addon: CatalogAddOn) => {
    if (!canManage) {
      GlobalSwal.fire({ text: lang.labels.addonNoPermission, icon: 'warning', confirmButtonText: lang.labels.okGotIt });
      return;
    }

    const addonId = addon.id ?? addon._id;
    if (!addonId) return;

    const price = formatPrice(addon);
    const name = addon.name ?? addonId;

    const confirmText = isTrial && trialEnd
      ? interpolate(lang.labels.addonActivateConfirmTextTrial, { price, name, date: fmtDate(trialEnd) })
      : interpolate(lang.labels.addonActivateConfirmText, { price, name });

    const result = await GlobalSwal.fire({
      title: interpolate(lang.labels.addonActivateConfirmTitle, { name }),
      html: `<p>${confirmText}</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.addonActivateConfirmButton,
      cancelButtonText: lang.labels.cancel,
    });
    if (!result.isConfirmed) return;

    const apiKey = authApiKey();
    if (!apiKey || !subId) {
      GlobalSwal.fire({ text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' });
      return;
    }

    setActionLoading(addonId);
    try {
      const response = await ApiDigitalAuthSubscription.activateAddon(apiKey, subId, addonId);
      if (response.status === 201 || response.status === 200) {
        // Refresh subscription snapshot so menu/permissions update without reconnecting
        await refreshSubscription();
        await loadActiveAddons();

        const noticeText = isTrial && trialEnd
          ? interpolate(lang.labels.addonActivatedInvoiceNoticeTrial, { date: fmtDate(trialEnd) })
          : lang.labels.addonActivatedInvoiceNotice;

        GlobalSwal.fire({
          title: interpolate(lang.labels.addonActivatedSuccess, { name }),
          text: noticeText,
          icon: 'success',
          confirmButtonText: lang.labels.okGotIt,
        });
      } else {
        const errCode = response.data?.code;
        let msg = response.data?.message ?? response.message;
        if (errCode === 'ADDON_ALREADY_ACTIVE') msg = lang.labels.addonAlreadyActive;
        if (errCode === 'ADDON_INCOMPATIBLE') msg = lang.labels.addonIncompatible;
        GlobalSwal.fire({ text: msg, icon: 'error', confirmButtonText: lang.labels.okGotIt });
      }
    } catch {
      GlobalSwal.fire({ text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeactivate = async (addon: CatalogAddOn, record: ActiveAddonRecord) => {
    if (!canManage) {
      GlobalSwal.fire({ text: lang.labels.addonNoPermission, icon: 'warning', confirmButtonText: lang.labels.okGotIt });
      return;
    }

    const addonId = addon.id ?? addon._id;
    if (!addonId) return;

    const name = addon.name ?? addonId;
    const periodEnd = fmtDate(record.current_period_end);

    const result = await GlobalSwal.fire({
      title: interpolate(lang.labels.addonDeactivateConfirmTitle, { name }),
      html: `<p>${interpolate(lang.labels.addonDeactivateConfirmText, { date: periodEnd })}</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.addonDeactivateConfirmButton,
      cancelButtonText: lang.labels.cancel,
    });
    if (!result.isConfirmed) return;

    const apiKey = authApiKey();
    if (!apiKey || !subId) {
      GlobalSwal.fire({ text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' });
      return;
    }

    setActionLoading(addonId);
    try {
      const response = await ApiDigitalAuthSubscription.deactivateAddon(apiKey, subId, addonId);
      if (response.status === 200) {
        await refreshSubscription();
        await loadActiveAddons();

        GlobalSwal.fire({
          title: interpolate(lang.labels.addonDeactivatedSuccess, { name }),
          icon: 'success',
          showConfirmButton: false,
          timer: 2200,
        });
      } else {
        GlobalSwal.fire({
          text: response.data?.message ?? response.message,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt,
        });
      }
    } catch {
      GlobalSwal.fire({ text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  if (!mounted) return null;

  // Filter addons compatible with current plan (show all, mark incompatible ones)
  const sortedAddons = [...addons].sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));

  const isLoading = catalogLoading || loadingActive;

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div className="card card-flush mb-5">
        <div className="card-header">
          <div className="card-title">
            <h3 className="fw-bolder">{lang.labels.addonSectionTitle}</h3>
          </div>
        </div>
        <div className="card-body">
          {isLoading ? (
            <div className="text-center py-10">
              <span className="spinner-border spinner-border-sm text-primary"></span>
            </div>
          ) : sortedAddons.length === 0 ? (
            <p className="text-gray-600 fs-6">{lang.labels.addonNoneAvailable}</p>
          ) : (
            <div className="row g-5">
              {sortedAddons.map(addon => {
                const addonId = addon.id ?? addon._id ?? '';
                const compatible = isCompatible(addon);
                const record = findActiveRecord(addonId);
                const isActive = !!record;
                const isPendingCancel = isActive && record!.cancel_at_period_end;
                const features = addonFeatureNames(addon);
                const price = formatPrice(addon);
                const isActioning = actionLoading === addonId;

                return (
                  <div className="col-md-6 col-lg-4" key={addonId}>
                    <div className={`border rounded p-5 h-100 d-flex flex-column ${!compatible ? 'bg-light' : ''}`}>
                      {/* Header: name + badge */}
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <h4 className="fw-bolder mb-0">{addon.name ?? addonId}</h4>
                        {isPendingCancel ? (
                          <span className="badge badge-light-warning fs-8">{lang.labels.addonPendingCancellation}</span>
                        ) : isActive ? (
                          <span className="badge badge-light-success fs-8">{lang.labels.addonActive}</span>
                        ) : compatible ? (
                          <span className="badge badge-light-primary fs-8">{lang.labels.addonAvailable}</span>
                        ) : (
                          <span className="badge badge-light-danger fs-8">{lang.labels.addonIncompatible}</span>
                        )}
                      </div>

                      {/* Description */}
                      {addon.description && (
                        <p className="text-gray-600 fs-7 mb-3">{addon.description}</p>
                      )}

                      {/* Features it provides */}
                      {features && (
                        <div className="text-gray-500 fs-8 mb-3">{features}</div>
                      )}

                      {/* Price — from server, never hardcoded */}
                      <div className="fw-bolder fs-5 text-gray-800 mb-4">{price}</div>

                      {/* Active period info */}
                      {isActive && record!.current_period_end && (
                        <div className="text-gray-500 fs-8 mb-3">
                          {interpolate(lang.labels.addonActiveUntil, { date: fmtDate(record!.current_period_end) })}
                        </div>
                      )}

                      {/* Spacer to push button to bottom */}
                      <div className="mt-auto">
                        {canManage ? (
                          <>
                            {isActive && !isPendingCancel ? (
                              <button
                                className="btn btn-sm btn-light-danger w-100"
                                type="button"
                                disabled={isActioning}
                                onClick={() => handleDeactivate(addon, record!)}
                              >
                                {isActioning ? (
                                  <span className="spinner-border spinner-border-sm me-2"></span>
                                ) : null}
                                {lang.labels.addonDeactivate}
                              </button>
                            ) : isPendingCancel ? (
                              <div className="text-gray-500 fs-8 text-center">
                                {interpolate(lang.labels.addonActiveUntil, { date: fmtDate(record!.current_period_end) })}
                              </div>
                            ) : compatible ? (
                              <button
                                className="btn btn-sm btn-primary w-100"
                                type="button"
                                disabled={isActioning}
                                onClick={() => handleActivate(addon)}
                              >
                                {isActioning ? (
                                  <span className="spinner-border spinner-border-sm me-2"></span>
                                ) : null}
                                {lang.labels.addonActivate}
                              </button>
                            ) : (
                              <div className="text-gray-500 fs-8 text-center">{lang.labels.addonIncompatible}</div>
                            )}
                          </>
                        ) : (
                          <div className="text-gray-500 fs-8 text-center">{lang.labels.addonNoPermission}</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionAddonsPage;
